import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchPublicRoleListApi } from '../../services/api';
import './LiveOpportunities.css';

const LiveOpportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const { data } = await fetchPublicRoleListApi();
        const fullData = data.data?.slice(0, 10) || []; 
        setOpportunities(fullData);
      } catch (err) {
        console.error('Fetch Error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadRoles();
  }, []);

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 1,
    arrows: false, /* Hide default arrows */
    responsive: [
      { breakpoint: 1600, settings: { slidesToShow: 5 } },
      { breakpoint: 1200, settings: { slidesToShow: 4 } },
      { breakpoint: 992,  settings: { slidesToShow: 3 } },
      { breakpoint: 768,  settings: { slidesToShow: 2 } },
      { breakpoint: 480,  settings: { slidesToShow: 1.5 } }
    ]
  };

  const getExpiryText = (endDate) => {
    if (!endDate) return 'No Deadline';
    const today = new Date();
    const expiryDate = new Date(endDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Closed';
    if (diffDays === 0) return 'Closes Today';
    if (diffDays === 1) return 'Closes Tomorrow';
    return `Closes in ${diffDays} days`;
  };

  return (
    <section className="live-opp-wrapper">
      <div className="live-opp-inner">
        <div className="live-opp-header">
          <h2 className="section-title text-purple" style={{margin:0, fontSize: '1.1rem', fontWeight: 800, letterSpacing: '0.5px'}}>LIVE OPPORTUNITIES</h2>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex gap-2 nav-arrows-wrapper">
              <button onClick={() => sliderRef.current?.slickPrev()} className="btn-nav-arrow"><ChevronLeft size={20}/></button>
              <button onClick={() => sliderRef.current?.slickNext()} className="btn-nav-arrow"><ChevronRight size={20}/></button>
            </div>
            <a href="/roles" className="view-all-link">
              View All <ArrowRight size={16} style={{marginLeft: '4px'}}/>
            </a>
          </div>
        </div>

        {loading ? (
          <div className="d-flex gap-3 overflow-hidden p-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="skeleton-card" style={{
                minWidth: '180px',
                height: '240px',
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeleton-loading 1.5s infinite',
                borderRadius: '12px'
              }}></div>
            ))}
          </div>
        ) : (
          <div className="live-opp-slider-container">
            <Slider ref={sliderRef} {...sliderSettings}>
              {opportunities.map((audition, index) => {
                const projectName = audition.projectName || 'Untitled';
                const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(projectName)}&background=050506&color=fff&size=20`;
                const displayImage = audition.projectImageUrl || defaultAvatar;
                
                const roleCategory = audition.roleCategory || 'Audition';
                const roleTitle = audition.roleTitle || 'Talent';
                const location = audition.location || 'Online';
                const ageGroup = audition.ageGroup || '18-30 yrs'; // Fallback if missing
                
                const finalAmount = Number(audition?.roleCost?.totalAmount || audition.finalAmount || audition.final_amount || 0);
                const payText = finalAmount > 0 ? `₹${finalAmount}` : 'Unpaid';
                
                const expiryText = getExpiryText(audition.projectEndDate);

                const formatType = (text) => {
                  if (!text || text === 'N/A') return 'Audition';
                  return text.replace(/([A-Z])/g, ' $1').trim();
                };
              
                const auditionTypes = audition.auditionType && audition.auditionType !== 'N/A'
                  ? audition.auditionType.split(';').map((t) => formatType(t))
                  : ['Audition'];

                return (
                  <div key={audition.id || index} className="live-opp-card-wrapper">
                    <div className="live-opp-card" onClick={() => navigate(`/audition/${audition.id}/${audition.urlSlug || 'details'}`)} style={{cursor: 'pointer'}}>
                      <div className="live-opp-image-box">
                        <img src={displayImage} alt={projectName} className="live-opp-img" />
                        <div className="live-opp-gradient"></div>
                        
                        <div className="live-opp-top-badges">
                          <div className="live-opp-strip-left">
                            {auditionTypes.map((type, idx) => (
                              <span key={idx} className="live-opp-type-badge">{type}</span>
                            ))}
                          </div>
                          <div className="live-opp-strip-right">
                            <span className="live-opp-cat-badge">{roleCategory}</span>
                          </div>
                        </div>
                        
                        <div className="live-opp-overlay-content">
                          <span className="opp-card-category">{roleCategory}</span>
                          <h3 className="opp-card-title">{projectName}</h3>
                          <span className="opp-card-role">{roleTitle}</span>
                          <span className="opp-card-details">{ageGroup} | {location}</span>
                        </div>
                      </div>
                      <div className="live-opp-footer">
                        <div className="opp-card-pay">{payText}</div>
                        <div className="opp-card-status text-red">{expiryText}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>
        )}
      </div>
    </section>
  );
};

export default LiveOpportunities;
